import { Service, inject } from "@angular/core";
import {environment} from "../../environments/environment";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {GoogleService} from "./google-service";
import {Observable, Subject} from "rxjs";
import {UserPerformanceDefaults} from "../model/user-performance-defaults";
import {EntityPerformanceDefaults} from "../model/entity-performance-defaults";
import {PerformanceDefaults} from "../model/performance-defaults";

@Service()
export class PerformanceDefaultsService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);


  private readonly urlBase: string = environment.serviceUrl + 'performanceDefaults/';
  private entityDefaultsChanged: Subject<EntityPerformanceDefaults> = new Subject<EntityPerformanceDefaults>();

  getUserDefaults(): Observable<UserPerformanceDefaults> {
    const url: string = this.urlBase + 'user';
    return this.http.get<UserPerformanceDefaults>(url, {headers: this.getRequestHeaders()});
  }

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }

  getEntityDefaults(entityId: string, revision: number): Observable<EntityPerformanceDefaults> {
    const url: string = this.urlBase + 'entity/' + entityId + '/' + revision;
    return this.http.get<EntityPerformanceDefaults>(url, {headers: this.getRequestHeaders()});
  }

  getDefaultValues(entityId: string, revision: number): Observable<PerformanceDefaults> {
    const url: string = this.urlBase + 'values/' + entityId + '/' + revision;
    return this.http.get<PerformanceDefaults>(url, {headers: this.getRequestHeaders()});
  }

  saveUserDefaults(defaults: UserPerformanceDefaults): Observable<UserPerformanceDefaults> {
    const url: string = this.urlBase + 'user';
    const body: string = JSON.stringify(defaults);
    return this.http.post<UserPerformanceDefaults>(url, body, {headers: this.getPostHeaders()});
  }

  getEntityDefaultsChangedObservable(): Observable<EntityPerformanceDefaults> {
    return this.entityDefaultsChanged.asObservable();
  }

  private getPostHeaders(contentType: string = 'application/json'): HttpHeaders {
    return this.getRequestHeaders().set("Content-Type", contentType);
  }

  saveEntityDefaults(defaults: EntityPerformanceDefaults): Observable<EntityPerformanceDefaults> {
    const subject: Subject<EntityPerformanceDefaults> = new Subject<EntityPerformanceDefaults>();
    const url: string = this.urlBase + 'entity';
    const body: string = JSON.stringify(defaults);
    this.http.post<EntityPerformanceDefaults>(url, body, {headers: this.getPostHeaders()}).subscribe({
      next: (updated: EntityPerformanceDefaults) => {
        this.entityDefaultsChanged.next(updated);
        subject.next(updated);
      }, error: (error: any) => subject.error(error)
    });
    return subject.asObservable();
  }

  deleteUserDefaults(): Observable<string> {
    const url: string = this.urlBase + 'user';
    return this.http.delete(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  deleteEntityDefaults(entityId: string, revision: number): Observable<string> {
    const url: string = this.urlBase + 'entity/' + entityId + '/' + revision;
    return this.http.delete(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

}
